/**
 * Braze SDK integration with consent management
 * All Braze operations respect user marketing consent preferences (GDPR compliance)
 *
 * @see https://www.braze.com/docs/developer_guide/platform_integration_guides/web/initial_sdk_setup/
 */

import * as braze from '@braze/web-sdk';
import config from 'config';
import { UserType } from 'types/user.types';

import store from '../store';

// Braze configuration from runtime config
export const brazeApiKey = config.get('braze.apiKey');
export const brazeEndpoint = config.get('braze.endpoint');

/**
 * Check if user has consented to marketing communications
 */
function hasMarketingConsent(user: UserType | null | undefined): boolean {
  if (!user) {
    return false;
  }

  return user.consentMarketingCommunications === true;
}

/**
 * Construct Braze user ID from user object
 * Format: {COUNTRY}_{USER_ID} (e.g., "EE_123456", "ZA_789012")
 * This matches the mobile app format for cross-platform tracking
 */
function getBrazeUserId(user: UserType): string {
  return user.globalId;
}

/**
 * Braze SDK wrapper with consent management
 */
export const Braze = {
  /**
   * Track if Braze has been initialized
   */
  _initialized: false,

  /**
   * Initialize Braze SDK
   * Call this once when the app loads (in _app.tsx)
   * @param options - Optional Braze initialization options
   */
  initialize: (options?: braze.InitializationOptions) => {
    try {
      if (typeof window === 'undefined') {
        // Server-side rendering - skip initialization
        return;
      }

      if (Braze._initialized) {
        return;
      }

      if (!brazeApiKey) {
        console.error('Braze: Missing API key in configuration');

        return;
      }

      if (!brazeEndpoint) {
        console.error('Braze: Missing endpoint in configuration');

        return;
      }

      const defaultOptions: braze.InitializationOptions = {
        baseUrl: brazeEndpoint,
        enableLogging: config.get('env') === 'dev',
        ...options,
      };

      braze.initialize(brazeApiKey, defaultOptions);
      Braze._initialized = true;
    } catch (error) {
      console.error('Braze: Failed to initialize SDK', error);
    }
  },

  /**
   * Identify user in Braze (respects consent)
   * @param user - User object (constructs ID as {COUNTRY}_{USER_ID})
   */
  changeUser: (user: UserType | null | undefined) => {
    try {
      if (!Braze._initialized) {
        return;
      }

      if (!user) {
        return;
      }

      if (!hasMarketingConsent(user)) {
        return;
      }

      const userId = getBrazeUserId(user);

      braze.changeUser(userId);

      // Open session after changing user
      braze.openSession();
    } catch (error) {
      console.error('Braze: Failed to change user', error);
    }
  },

  /**
   * Log custom event in Braze (respects consent)
   * @param eventName - Name of the event (e.g., 'checkout_started')
   * @param properties - Event properties (optional)
   */
  logCustomEvent: (eventName: string, properties?: Record<string, any>) => {
    try {
      if (!Braze._initialized) {
        return;
      }

      const user = store?.getState()?.userData?.user;

      if (!user) {
        return;
      }

      if (!hasMarketingConsent(user)) {
        return;
      }

      braze.logCustomEvent(eventName, properties);
    } catch (error) {
      console.error(`Braze: Failed to log event: ${eventName}`, error);
    }
  },

  /**
   * Set custom user attribute in Braze (respects consent)
   * @param key - Attribute key
   * @param value - Attribute value
   */
  setCustomUserAttribute: (key: string, value: string | number | boolean | Date | null) => {
    try {
      if (!Braze._initialized) {
        return;
      }

      const user = store?.getState()?.userData?.user;

      if (!user) {
        return;
      }

      if (!hasMarketingConsent(user)) {
        return;
      }

      const brazeUser = braze.getUser();

      if (brazeUser) {
        brazeUser.setCustomUserAttribute(key, value);
      }
    } catch (error) {
      console.error(`Braze: Failed to set attribute: ${key}`, error);
    }
  },

  /**
   * Request push notification permission and register token with Braze
   * Call this after user has logged in and granted consent
   */
  requestPushPermission: async () => {
    try {
      if (!Braze._initialized) {
        return;
      }

      const user = store?.getState()?.userData?.user;

      if (!user) {
        return;
      }

      if (!hasMarketingConsent(user)) {
        return;
      }

      // Request push notification permission
      braze.requestPushPermission();
    } catch (error) {
      console.error('Braze: Failed to request push permission', error);
    }
  },

  /**
   * Destroy Braze session (call on logout)
   */
  destroySession: () => {
    try {
      if (!Braze._initialized) {
        return;
      }

      braze.destroy();
    } catch (error) {
      console.error('Braze: Failed to destroy session', error);
    }
  },
};

/**
 * Braze event names enum (similar to GTMEvents)
 * Add new event names here as needed
 */
export enum BrazeEvents {
  CHECKOUT_STARTED = 'checkout_started',
  PRODUCT_VIEWED = 'product_viewed',
  SEARCH_PERFORMED = 'search_performed',
  // Add more events as needed
}
